import axios from "axios"
import FsLightbox from "fslightbox-react"
import React, { Suspense, useEffect, useState } from "react"
import * as Icon from "react-feather"
import { Helmet } from "react-helmet"
import Slider from "react-slick"
import Layout from "../components/Layout"
import Sectiontitle from "../components/Sectiontitle"
import Service from "../components/Service"
import Spinner from "../components/Spinner"
import Testimonial from "../components/Testimonial"
import Image from "../components/Image"

interface Information {
  name: string
  age?: number
  phone?: string
  nationality?: string
  language?: string
  email?: string
  address?: string
  freelanceStatus?: string
  aboutImage: string
  aboutImageLg: string
  aboutContent: string
  personalContent: string
  cvLink: string
}

interface Interest {
  title: string
  [key: string]: unknown
}

interface Review {
  id: string | number
  [key: string]: unknown
}

const About: React.FC = () => {
  const [toggler, setToggler] = useState(false)
  const [information, setInformation] = useState<Information>({
    name: "",
    aboutImage: "",
    aboutImageLg: "",
    aboutContent: "",
    personalContent: "",
    cvLink: ""
  })
  const [interests, setInterests] = useState<Interest[]>([])
  const [reviews, setReviews] = useState<Review[]>([])

  const sliderSettings = {
    dots: false,
    infinite: true,
    arrows: false,
    speed: 500,
    slidesToShow: 2,
    slidesToScroll: 2,
    autoplay: true,
    autoplaySpeed: 6000,
    pauseOnHover: true,
    adaptiveHeight: true,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1
        }
      }
    ]
  }

  const handleToggler = (event: React.MouseEvent) => {
    setToggler(!toggler)
  }

  useEffect(() => {
    axios.get<Information>("/api/information").then((response) => {
      setInformation(response.data)
    })
    axios.get<Interest[]>("/api/interests").then((response) => {
      setInterests(response.data)
    })
    axios.get<Review[]>("/api/reviews").then((response) => {
      setReviews(response.data)
    })
  }, [])

  return (
    <Layout>
      <Helmet>
        <title>About - Nathan Hensher</title>
        <meta
          name="description"
          content="Nathan Hensher - Javascript Engineer - About"
        />
      </Helmet>
      <Suspense fallback={<Spinner />}>
        <div className="mi-about-area mi-section mi-padding-top">
          <div className="container">
            <Sectiontitle title="About Me" />
            <div className="row align-items-center">
              <div className="col-lg-6">
                <div className="mi-about-image">
                  <Image
                    src={information.aboutImage}
                    alt="About Nathan Hensher"
                  />
                  <span className="mi-about-image-icon" onClick={handleToggler}>
                    <Icon.ZoomIn />
                  </span>
                  <FsLightbox
                    toggler={toggler}
                    sources={[information.aboutImageLg]}
                  />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="mi-about-content">
                  <h3>
                    I'm <span className="color-theme">{information.name}</span>
                  </h3>
                  <p>{information.aboutContent}</p>
                  <p>{information.personalContent}</p>
                  <ul>
                    {!information.name ? null : (
                      <li>
                        <b>Full Name</b> {information.name}
                      </li>
                    )}
                    {!information.age ? null : (
                      <li>
                        <b>Age</b> {information.age} Years
                      </li>
                    )}
                    {!information.phone ? null : (
                      <li>
                        <b>Phone</b> {information.phone}
                      </li>
                    )}
                    {!information.nationality ? null : (
                      <li>
                        <b>Nationality</b> {information.nationality}
                      </li>
                    )}
                    {!information.language ? null : (
                      <li>
                        <b>Languages</b> {information.language}
                      </li>
                    )}
                    {!information.email ? null : (
                      <li>
                        <b>Email</b> {information.email}
                      </li>
                    )}
                    {!information.address ? null : (
                      <li>
                        <b>Address</b> {information.address}
                      </li>
                    )}
                    {!information.freelanceStatus ? null : (
                      <li>
                        <b>Freelance</b> {information.freelanceStatus}
                      </li>
                    )}
                  </ul>
                  <a
                    href={information.cvLink}
                    className="mi-button"
                    target="_blank"
                    rel="noreferrer"
                  >
                    See CV
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mi-service-area mi-section mi-padding-top">
          <div className="container">
            <Sectiontitle title="Interests" />
            <div className="mi-service-wrapper">
              <div className="row mt-30-reverse">
                {interests.map((interest) => (
                  <div
                    className="col-lg-4 col-md-6 col-12 mt-30"
                    key={interest.title}
                  >
                    <Service content={interest as any} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="mi-review-area mi-section mi-padding-top mi-padding-bottom">
          <div className="container">
            <Sectiontitle title="Recommendations" />
            <div className="row justify-content-center">
              <div className="col-12">
                <Slider className="mi-testimonial-slider" {...sliderSettings}>
                  {reviews.map((review) => (
                    <Testimonial key={review.id} content={review as any} />
                  ))}
                </Slider>
              </div>
            </div>
          </div>
        </div>
      </Suspense>
    </Layout>
  )
}

export default About
